import StickyTableResponsive from '../../common/StickyTableResponsive';
import SortableTh from '../../common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import {
  AdminDeactivateModal,
  AdminDeleteModal,
  AdminPeriodFields,
  AdminValidityActions,
  AdminValidityBadge,
  defaultAdminPeriod,
} from './KkpAdminShared';
import AdminFormLabel from '../../admin/AdminFormLabel';
import SearchableSelect from '../../SearchableSelect';
import KkpPlanningMagInlineCell from '../KkpPlanningMagInlineCell';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';
import {
  buildProduitRefLabelMap,
  buildProduitRefOptions,
} from '../../../utils/adminProduitRefOptions';
import clientAnnuaireService from '../../../services/clientAnnuaireService';
import kkpService from '../../../services/kkpService';
import { notify } from '../../common/AppToast';
import { AppButton, AppInput, AppModal, AppSpinner } from '../../ui';

const VALIDITY_REF_SORT = {
  valeur: { type: 'string', getValue: (row) => row.value },
  productRef: { type: 'string', getValue: (row) => row.product_ref },
  periode: { type: 'string', getValue: (row) => row.validity_label },
  statut: { type: 'string', getValue: (row) => row.validity_status },
};

const emptySetForm = () => ({
  value: '',
  product_ref: '',
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const KkpAdminSetSection = () => {
  const [data, setData] = useState({ set: {}, mois: [] });
  const [catalogueProduits, setCatalogueProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [setForm, setSetForm] = useState(emptySetForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePeriod, setDeactivatePeriod] = useState(defaultAdminPeriod);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const moisOptions = data.mois ?? [];
  const setList = data.set?.formats ?? [];
  const { sortedRows: sortedSet, sort: sortSet, toggleSort: toggleSet, getSortIcon: getSetIcon } = useTableSort(setList, VALIDITY_REF_SORT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result, catalogue] = await Promise.all([
        kkpService.listPlanningAdmin(),
        clientAnnuaireService.listProduits(),
      ]);
      setData({ set: result.set ?? {}, mois: result.mois ?? [] });
      setCatalogueProduits(catalogue);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const produitRefOptions = useMemo(
    () => buildProduitRefOptions(
      catalogueProduits,
      [
        setForm.product_ref,
        ...setList.map((item) => item.product_ref),
      ],
    ),
    [catalogueProduits, setForm.product_ref, setList],
  );
  const produitRefLabelByValue = useMemo(
    () => buildProduitRefLabelMap(produitRefOptions),
    [produitRefOptions],
  );

  const wrapSave = async (fn, successMessage) => {
    setSaving(true);
    try {
      await fn();
      if (successMessage) notify.success(successMessage);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    setSetForm(emptySetForm());
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSetForm(emptySetForm());
  };

  const submitCreate = () => wrapSave(
    () => kkpService.createPlanningSetFormat({
      ...setForm,
      product_ref: setForm.product_ref || null,
    }).then(() => closeModal()),
    'Format ajouté',
  );

  const clearInlineEdit = () => {
    setEditingCell(null);
    setEditDraft(null);
  };

  const startInlineEdit = (id, field) => {
    const row = setList.find((item) => item.id === id);
    if (!row) return;
    setEditingCell({ venteId: id, field });
    setEditDraft(field === 'product_ref' ? String(row.product_ref || '').toUpperCase() : (row[field] ?? ''));
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const { venteId, field } = editingCell;
    const row = setList.find((item) => item.id === venteId);
    if (!row || (field !== 'product_ref' && field !== 'value')) {
      clearInlineEdit();
      return;
    }

    const draft = draftOverride !== undefined ? draftOverride : editDraft;

    if (field === 'value') {
      const nextValue = String(draft ?? '').trim();
      if (!nextValue) {
        notify.error('Le libellé est requis.');
        return;
      }
      if (nextValue === row.value) {
        clearInlineEdit();
        return;
      }

      setInlineSaving(true);
      try {
        const option = await kkpService.updatePlanningOption(venteId, { value: nextValue });
        setData((prev) => ({
          ...prev,
          set: {
            ...prev.set,
            formats: (prev.set?.formats ?? []).map((item) => (
              item.id === venteId ? { ...item, ...(option ?? {}), value: option?.value ?? nextValue } : item
            )),
          },
        }));
        clearInlineEdit();
        notify.success('Format enregistré');
      } catch (err) {
        notify.error(err.message);
      } finally {
        setInlineSaving(false);
      }
      return;
    }

    const nextRef = String(draft ?? '').trim().toUpperCase() || null;
    const prevRef = row.product_ref ? String(row.product_ref).toUpperCase() : null;
    if (nextRef === prevRef) {
      clearInlineEdit();
      return;
    }

    setInlineSaving(true);
    try {
      await kkpService.updatePlanningOption(venteId, { product_ref: nextRef });
      setData((prev) => ({
        ...prev,
        set: {
          ...prev.set,
          formats: (prev.set?.formats ?? []).map((item) => (
            item.id === venteId ? { ...item, product_ref: nextRef } : item
          )),
        },
      }));
      clearInlineEdit();
      notify.success('Réf. produit enregistrée');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await kkpService.deactivatePlanningOption(deactivateTarget.id, deactivatePeriod);
      setDeactivateTarget(null);
      notify.success('Option désactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (id) => {
    setSaving(true);
    try {
      await kkpService.reactivatePlanningOption(id);
      notify.success('Option réactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await kkpService.deletePlanningOption(deleteTarget.id);
      setDeleteTarget(null);
      notify.success('Élément supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppSpinner />;

  return (
    <>
      <div className="card admin-data-card">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>Formats SET ({sortedSet.length})</span>
          <AppButton
            size="sm"
            variant="danger-600"
            icon="mdi:plus"
            disabled={saving || inlineSaving}
            onClick={openCreateModal}
          >
            Ajouter
          </AppButton>
        </div>
        <StickyTableResponsive>
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <SortableTh sortKey="valeur" label="Format" sort={sortSet} onSort={toggleSet} getSortIcon={getSetIcon} />
                <SortableTh sortKey="productRef" label="Réf. Dolibarr" sort={sortSet} onSort={toggleSet} getSortIcon={getSetIcon} />
                <SortableTh sortKey="periode" label="Période" sort={sortSet} onSort={toggleSet} getSortIcon={getSetIcon} />
                <SortableTh sortKey="statut" label="Statut" sort={sortSet} onSort={toggleSet} getSortIcon={getSetIcon} />
                <SortableTh label="Actions" className="text-end" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {sortedSet.map((item) => (
                <tr key={item.id} className={item.validity_status === 'expired' ? 'opacity-50' : ''}>
                  <KkpPlanningMagInlineCell
                    venteId={item.id}
                    field="value"
                    type="text"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="fw-medium"
                  >
                    {item.value}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={item.id}
                    field="product_ref"
                    type="searchable-select"
                    options={produitRefOptions}
                    allowEmpty
                    emptyLabel="— Aucune —"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-sm"
                  >
                    {produitRefLabelByValue.get(String(item.product_ref || '').toUpperCase()) || item.product_ref || '—'}
                  </KkpPlanningMagInlineCell>
                  <td className="text-sm">{item.validity_label}</td>
                  <td><AdminValidityBadge status={item.validity_status} /></td>
                  <td className="text-end">
                    <AdminValidityActions
                      item={item}
                      saving={saving || inlineSaving}
                      onDeactivate={(t) => { setDeactivateTarget(t); setDeactivatePeriod(defaultAdminPeriod()); }}
                      onReactivate={handleReactivate}
                      onDelete={(t) => setDeleteTarget(t)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyTableResponsive>
      </div>

      <AppModal
        show={modalOpen}
        title="Ajouter un format SET"
        onClose={closeModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitCreate();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant="danger-600" loading={saving}>
              Ajouter
            </AppButton>
          </>
        )}
      >
        <p className="text-secondary-light small mb-3">
          Chaque format actif = une ligne par mois dans le planning
        </p>
        <div className="row g-3">
          <div className="col-md-6">
            <AdminFormLabel help={ADMIN_FIELD_HELP.setFormatLabel.help} helpExample={ADMIN_FIELD_HELP.setFormatLabel.helpExample} required>
              Format
            </AdminFormLabel>
            <AppInput required placeholder="Ex: CARRE 1 G" value={setForm.value} onChange={(e) => setSetForm((f) => ({ ...f, value: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <AdminFormLabel help={ADMIN_FIELD_HELP.productRef?.help} helpExample={ADMIN_FIELD_HELP.productRef?.helpExample}>
              Réf. produit Dolibarr
            </AdminFormLabel>
            <SearchableSelect
              value={setForm.product_ref}
              onChange={(value) => setSetForm((f) => ({ ...f, product_ref: value }))}
              options={produitRefOptions}
              allowEmpty
              emptyOptionLabel="— Aucune —"
              placeholder="Produit en vente…"
              emptyLabel="Aucun produit catalogue"
              disabled={saving}
            />
          </div>
          <AdminPeriodFields
            year={setForm.valid_from_year}
            month={setForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(y) => setSetForm((f) => ({ ...f, valid_from_year: y }))}
            onMonthChange={(m) => setSetForm((f) => ({ ...f, valid_from_month: m }))}
          />
        </div>
      </AppModal>

      <AdminDeactivateModal
        target={deactivateTarget}
        period={deactivatePeriod}
        moisOptions={moisOptions}
        saving={saving}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        onPeriodChange={setDeactivatePeriod}
      />
      <AdminDeleteModal
        target={deleteTarget}
        contextKey="set-format"
        saving={saving}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default KkpAdminSetSection;
